import {
    PageQueryDocument,
    PagesQueryDocument,
    PageQueryQuery,
    PageFragmentFragment,
    PaginationArg,
    PublicationStatus,
} from "api/cms-website/generated/graphql";
import {
    CmsPageContentProps,
    CmsWrapperProps,
} from "api/cms-website/cms-website.types";

import { I18nLocale, StrapiLocale, StrapiLocaleToI18n } from "constants/enum";

import { transformPageData } from "../helpers/transformers";
import { PAGE_COMPONENTS_PAGINATION } from "../queries/pages";
import { CmsGraphQLApi } from "./CmsGraphQLApi";

type PageFindOneParams = {
    slug: string;
    locale: string;
    isPreview?: boolean;
};

type PageFindAllParams = {
    pagination?: PaginationArg;
};

/**
 * API class for fetching page data from CMS using GraphQL
 */
export class PagesApi extends CmsGraphQLApi {
    /**
     * Find a single page by slug and locale
     *
     * @param params - Query parameters including slug, locale, and optional preview flag
     * @returns Page data or null if not found
     *
     * Preview vs prod: `status` (Draft/Published), not fetchPolicy; per-call client + default
     * no-cache replaces the old preview-only no-cache vs cache-first split.
     */
    async findOne({
        slug,
        locale,
        isPreview,
    }: PageFindOneParams): Promise<CmsPageContentProps | null> {
        const response = await this.client.query({
            query: PageQueryDocument,
            variables: {
                filters: {
                    slug: {
                        eq: slug,
                    },
                },
                locale,
                status: isPreview
                    ? PublicationStatus.Draft
                    : PublicationStatus.Published,
                componentsPagination: PAGE_COMPONENTS_PAGINATION,
            },
            fetchPolicy: "no-cache",
        });

        const pages = (response.data as PageQueryQuery)?.pages;
        if (!Array.isArray(pages) || pages.length === 0) {
            return null;
        }

        // Type-safe: pages is PageFragmentFragment[]
        return transformPageData(pages as PageFragmentFragment[], locale);
    }

    /**
     * Get all pages with localizations for static path generation
     *
     * @param params - Query parameters including optional pagination options
     * @param params.pagination - Pagination options per locale query. Defaults to limit: 200
     * @returns All pages with their localizations
     */
    async findAll({
        pagination,
    }: PageFindAllParams = {}): Promise<CmsWrapperProps> {
        // Strapi 5 removed locale=all for collections; fetch published pages per locale and merge.
        // https://docs.strapi.io/cms/migration/v4-to-v5/breaking-changes/no-locale-all
        const paginationArg =
            pagination ||
            ({
                limit: 200,
            } satisfies PaginationArg);

        const strapiLocales = Object.values(StrapiLocale) as StrapiLocale[];

        const responses = await Promise.all(
            strapiLocales.map((locale) =>
                this.client.query({
                    query: PagesQueryDocument,
                    variables: {
                        locale,
                        pagination: paginationArg,
                        status: PublicationStatus.Published,
                    },
                    fetchPolicy: "no-cache",
                })
            )
        );

        const seen = new Set<string>();
        const pages: unknown[] = [];

        responses.forEach((response) => {
            const batch = (response.data as { pages?: unknown[] })?.pages;
            const list = Array.isArray(batch)
                ? batch.filter((p): p is Record<string, unknown> => p != null)
                : [];

            for (const page of list) {
                const docId = String(page.documentId ?? "");
                const loc = String(page.locale ?? "");
                const key = `${docId}\0${loc}`;
                if (seen.has(key)) continue;
                seen.add(key);
                pages.push(page);
            }
        });

        if (pages.length === 0) {
            return { data: [] };
        }

        type LocalizationRow = { slug: string; locale: I18nLocale };

        const strapiCodeToI18n = (code: unknown): I18nLocale | undefined => {
            if (typeof code !== "string" || code === "") return undefined;
            return StrapiLocaleToI18n[code as StrapiLocale];
        };

        const transformedPages: CmsPageContentProps[] = pages
            .filter((page: any) => page !== null && page !== undefined)
            .map((page: any): CmsPageContentProps | null => {
                const locale = strapiCodeToI18n(page.locale);
                if (!locale) {
                    return null;
                }

                const result: CmsPageContentProps = {
                    id: parseInt(page.documentId || "0", 10),
                    documentId: page.documentId || "",
                    name: page.name || "",
                    slug: page.slug || "",
                    locale,
                };

                if (page.localizations && Array.isArray(page.localizations)) {
                    result.localizations = page.localizations
                        .map((loc: any) => {
                            const locI18n = strapiCodeToI18n(loc?.locale);
                            if (!locI18n) return null;
                            return {
                                slug: loc.slug || "",
                                locale: locI18n,
                            };
                        })
                        .filter(
                            (
                                item: LocalizationRow | null
                            ): item is LocalizationRow => item != null
                        );
                }

                return result;
            })
            .filter((p): p is CmsPageContentProps => p != null);

        return { data: transformedPages };
    }
}
